import type {
  Account,
  Contact,
  Conversation,
  DisappearTimerSec,
  GroupRecord,
  MessageEvent,
  SecurityEvent,
} from "@veto/domain";
import type { WasmIdentity } from "@veto/crypto-bridge";
import type { RelayPool } from "@veto/transport";
import { useMemo, useState, type RefObject } from "react";
import { saveContact, upsertConversation } from "@veto/storage";
import { buildInviteLink } from "../messaging/inviteParse";
import { ConversationListPane, type MessageHit } from "./ConversationListPane";
import { ChatPane } from "./ChatPane";
import { ContactDetailsPane, GroupDetailsPane } from "./DetailsPane";
import { CreateGroupModal } from "./CreateGroupModal";
import { AddContactModal } from "./AddContactModal";
import { ModalShell } from "./ModalShell";
import { SettingsPanel, type SettingsTab } from "../screens/SettingsPanel";
import { ProfileModal } from "../screens/ProfileModal";
import type { ProfileInput } from "../hooks/useProfileActions";
import { InAppBanner } from "./InAppBanner";
import {
  markNotificationPromptShown,
  notificationPromptPending,
  type InAppBannerPayload,
  type NotificationPrefs,
  type NotificationPermissionState,
} from "../notifications";
import { NotificationPrompt } from "./NotificationPrompt";
import type { SecureVaultCapabilities } from "../secureVault";
import type { ChatRowAction } from "../hooks/useConversationChrome";
import { ConfirmHost } from "./ConfirmDialog";
import { ForwardModal } from "./ForwardModal";
import { StarredModal } from "./StarredModal";
import { ReportModal } from "./ReportModal";

export function AppShell(props: {
  account: Account | null;
  sessionIdentity: WasmIdentity | null;
  relayStatus: string;
  relayClient?: RelayPool | null;
  query: string;
  setQuery: (v: string) => void;
  conversations: Conversation[];
  filteredConversations: Conversation[];
  contacts: Contact[];
  activeId: string | null;
  activeConv: Conversation | null;
  activeContact: Contact | null;
  activeGroup: GroupRecord | null;
  messages: MessageEvent[];
  unreadAtOpen?: number;
  searchMessages?: (q: string) => Promise<MessageHit[]>;
  loadStarred?: (conversationId: string | null) => Promise<MessageHit[]>;
  chatQuery: string;
  setChatQuery: (v: string) => void;
  draft: string;
  setDraft: (v: string) => void;
  replyTo: MessageEvent | null;
  editing: MessageEvent | null;
  setReplyTo: (m: MessageEvent | null) => void;
  setEditing: (m: MessageEvent | null) => void;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  voiceUrlFor: (id: string) => string | undefined;
  showDetails: boolean;
  setShowDetails: (value: boolean | ((v: boolean) => boolean)) => void;
  showAdd: boolean;
  setShowAdd: (v: boolean) => void;
  showCreateGroup: boolean;
  setShowCreateGroup: (v: boolean) => void;
  showSettings: boolean;
  setShowSettings: (v: boolean) => void;
  settingsTab: SettingsTab;
  setSettingsTab: (t: SettingsTab) => void;
  unverifiedCount: number;
  openSecurityCount: number;
  securityEvents: SecurityEvent[];
  vaultCaps: SecureVaultCapabilities | null;
  notificationPrefs: NotificationPrefs;
  setNotificationPrefs: (p: NotificationPrefs) => void;
  saveNotificationPrefs: (p: NotificationPrefs) => void;
  notificationPermission: NotificationPermissionState;
  setNotificationPermission: (p: NotificationPermissionState) => void;
  requestNotificationPermission: () => Promise<NotificationPermissionState>;
  onLockNow?: () => void;
  /** Reload contacts, conversations and security events from storage. */
  refresh?: () => Promise<void>;
  /** Decrypted media held in memory this session (Settings → Storage & data). */
  mediaCacheCount?: number;
  onClearMediaCache?: () => number;
  banner: InAppBannerPayload | null;
  setBanner: (b: InAppBannerPayload | null) => void;
  toast: string | null;
  setToast: (t: string | null) => void;
  onOpenConversation: (id: string) => void;
  onSend: () => void;
  onAttachImage: (f: File) => void;
  onAttachDocument: (f: File) => void;
  onViewOnceImage: (f: File) => void;
  onSendSticker: (packId: string, stickerId: string) => void;
  onSendStickerFile: (f: File) => void;
  onSendGifFile: (f: File) => void;
  onVoice: (blob: Blob, ms: number) => void;
  onVoiceError?: (message: string) => void;
  onDirect: () => void;
  onReply: (m: MessageEvent) => void;
  onReact: (m: MessageEvent, emoji: string) => void;
  onEdit: (m: MessageEvent) => void;
  onDeleteMe: (m: MessageEvent) => void;
  onDeleteEveryone: (m: MessageEvent) => void;
  onForward: (m: MessageEvent) => void;
  onForwardTo?: (m: MessageEvent, targets: Conversation[]) => void;
  /** Block for a report (no second confirmation); declines a pending request instead when asked. */
  onReportBlock?: (contact: Contact, fromRequest: boolean) => Promise<void>;
  onStar: (m: MessageEvent) => void;
  onPin: (m: MessageEvent) => void;
  onOpenViewOnce: (m: MessageEvent) => void;
  onCreatePoll?: () => void;
  onVotePoll?: (m: MessageEvent, optionId: string) => void;
  onAddContact: (
    name: string,
    hybrid: string,
    messaging: string,
    transport?: string,
  ) => Promise<void>;
  onCreateGroup: (title: string, memberIds: string[]) => Promise<void>;
  onVerifyContact: () => void;
  onSaveProfile: (input: ProfileInput) => Promise<void>;
  onRemoveGroupMember: (hex: string) => void;
  onSetGroupRole?: (hex: string, role: "admin" | "member") => void;
  onAddGroupMembers?: (contactIds: string[]) => void;
  onRenameGroup?: (title: string) => void;
  onBack: () => void;
  onChatRowAction: (
    conv: Conversation,
    contact: Contact | null,
    action: ChatRowAction,
    value?: DisappearTimerSec,
  ) => void;
  onChatLockedCheck: (conversationId: string) => Promise<boolean>;
  onChatDisappearGet: (conversationId: string) => Promise<DisappearTimerSec>;
  disappearTimer: DisappearTimerSec;
  groupForkWarning: string | null;
  onTogglePin: () => void;
  onToggleMute: () => void;
  onArchive: () => void;
  onBlock: () => void;
  onAcceptRequest?: () => void;
  onDeclineRequest?: (block: boolean) => void;
  onAcceptGroupInvite?: () => void;
  onDeclineGroupInvite?: (block: boolean) => void;
  /** Start / open a direct chat with an existing contact (New chat sheet). */
  onOpenContact?: (contactId: string) => void;
  onDisappearChange: (sec: DisappearTimerSec) => void;
  onToggleChatLock: () => void;
  onClearActiveMessages: () => void;
  onDeleteActiveChat: () => void;
}) {
  // Our shareable invite link (fragment payload, never sent to the server).
  const myInviteLink = useMemo(() => {
    if (!props.sessionIdentity || !props.account) return null;
    try {
      return buildInviteLink(props.sessionIdentity, props.account.displayName);
    } catch {
      return null;
    }
  }, [props.sessionIdentity, props.account]);
  const p = props;
  // Profile lives in its own screen — opened from the top-bar avatar (SPEC §5.5).
  const [showProfile, setShowProfile] = useState(false);
  // Message being forwarded (chat picker open).
  const [forwarding, setForwarding] = useState<MessageEvent | null>(null);
  // Starred list: undefined = closed, null = all chats, id = one chat.
  const [starredFor, setStarredFor] = useState<string | null | undefined>(undefined);
  // A message to bring into view once its chat is open (starred list, search).
  const [focusMessageId, setFocusMessageId] = useState<string | null>(null);
  // Report & block in progress.
  const [reporting, setReporting] = useState<{
    reported?: MessageEvent;
    fromRequest?: boolean;
  } | null>(null);
  // One-time "Know when someone writes" sheet after the identity exists (phone parity).
  const [askNotify, setAskNotify] = useState(() => notificationPromptPending());

  function setNotifyPrefs(prefs: NotificationPrefs) {
    p.setNotificationPrefs(prefs);
    p.saveNotificationPrefs(prefs);
  }

  async function requestNotifications(): Promise<NotificationPermissionState> {
    const perm = await p.requestNotificationPermission();
    p.setNotificationPermission(perm);
    if (perm === "granted")
      p.setToast('Notifications on — they say "New encrypted message" unless you turn on previews');
    else if (perm === "denied")
      p.setToast("Notifications are blocked in your browser settings — you can allow them there");
    return perm;
  }
  return (
    <div className={`app-shell ${p.activeId ? "split chat-open" : ""}`}>
      <ConversationListPane
        account={p.account}
        relayStatus={p.relayStatus}
        query={p.query}
        setQuery={p.setQuery}
        conversations={p.conversations}
        filtered={p.filteredConversations}
        contacts={p.contacts}
        activeId={p.activeId}
        onOpen={p.onOpenConversation}
        searchMessages={p.searchMessages}
        onOpenAtMessage={(conversationId, q) => {
          // Open the chat, then let its search land on the match (it highlights and steps).
          void Promise.resolve(p.onOpenConversation(conversationId)).then(() => p.setChatQuery(q));
        }}
        onSettings={() => p.setShowSettings(true)}
        onOpenProfile={() => setShowProfile(true)}
        onAddContact={() => p.setShowAdd(true)}
        onNewGroup={() => p.setShowCreateGroup(true)}
        onChatRowAction={p.onChatRowAction}
        onChatLockedCheck={p.onChatLockedCheck}
        onChatDisappearGet={p.onChatDisappearGet}
        onOpenDetails={(id) => {
          p.onOpenConversation(id);
          p.setShowDetails(() => true);
        }}
      />

      <ChatPane
        onAcceptRequest={p.onAcceptRequest}
        onDeclineRequest={p.onDeclineRequest}
        onAcceptGroupInvite={p.onAcceptGroupInvite}
        onDeclineGroupInvite={p.onDeclineGroupInvite}
        activeGroup={p.activeConv?.type === "group" ? p.activeGroup : null}
        contacts={p.contacts}
        selfKeyHex={p.account?.messagingPublicKeyHex ?? ""}
        activeConv={p.activeConv}
        activeContact={p.activeContact}
        messages={p.messages}
        unreadAtOpen={p.unreadAtOpen ?? 0}
        focusMessageId={focusMessageId}
        disappearTimer={p.disappearTimer}
        onReport={
          p.onReportBlock && p.activeConv?.type === "direct"
            ? (opts) => setReporting(opts)
            : undefined
        }
        onFocusDone={() => setFocusMessageId(null)}
        chatQuery={p.chatQuery}
        setChatQuery={p.setChatQuery}
        draft={p.draft}
        setDraft={p.setDraft}
        replyTo={p.replyTo}
        editing={p.editing}
        clearComposerMeta={() => {
          p.setReplyTo(null);
          p.setEditing(null);
        }}
        messagesEndRef={p.messagesEndRef}
        voiceUrlFor={p.voiceUrlFor}
        onBack={p.onBack}
        onToggleDetails={() => p.setShowDetails((v) => !v)}
        onOpenDetails={() => p.setShowDetails(true)}
        onSend={p.onSend}
        onAttachImage={p.onAttachImage}
        onAttachDocument={p.onAttachDocument}
        onViewOnceImage={p.onViewOnceImage}
        onSendSticker={p.onSendSticker}
        onSendStickerFile={p.onSendStickerFile}
        onSendGifFile={p.onSendGifFile}
        onVoice={p.onVoice}
        onVoiceError={p.onVoiceError}
        onDirect={p.onDirect}
        onReply={p.onReply}
        onReact={p.onReact}
        onEdit={p.onEdit}
        onDeleteMe={p.onDeleteMe}
        onDeleteEveryone={p.onDeleteEveryone}
        onForward={(m) => (p.onForwardTo ? setForwarding(m) : p.onForward(m))}
        onStar={p.onStar}
        onPin={p.onPin}
        onOpenViewOnce={p.onOpenViewOnce}
        onCreatePoll={p.onCreatePoll}
        onVotePoll={p.onVotePoll}
      />

      {p.showDetails && p.activeContact && (
        <ModalShell
          title={p.activeContact.displayName}
          ariaLabel="Contact details"
          onClose={() => p.setShowDetails(() => false)}
          size="sheet"
          className="details-modal"
        >
          <ContactDetailsPane
            contact={p.activeContact}
            conversation={p.activeConv}
            disappearTimer={p.disappearTimer}
            onVerify={p.onVerifyContact}
            onTogglePin={p.onTogglePin}
            onToggleMute={p.onToggleMute}
            onArchive={p.onArchive}
            onBlock={p.onBlock}
            onDisappearChange={p.onDisappearChange}
            onToggleChatLock={p.onToggleChatLock}
            onClearMessages={p.onClearActiveMessages}
            onDeleteChat={p.onDeleteActiveChat}
            onShowStarred={
              p.loadStarred ? () => setStarredFor(p.activeConv?.id ?? null) : undefined
            }
            onReport={
              p.onReportBlock
                ? () => {
                    p.setShowDetails(() => false);
                    setReporting({});
                  }
                : undefined
            }
          />
        </ModalShell>
      )}

      {p.showDetails && p.activeGroup && p.activeConv?.type === "group" && (
        <ModalShell
          title={p.activeGroup.title}
          ariaLabel="Group details"
          onClose={() => p.setShowDetails(() => false)}
          size="sheet"
          className="details-modal"
        >
          <GroupDetailsPane
            group={p.activeGroup}
            forkWarning={p.groupForkWarning}
            selfIsAdmin={
              !!p.account &&
              p.activeGroup.members.some(
                (m) => m.identityKeyHex === p.account!.messagingPublicKeyHex && m.role === "admin",
              )
            }
            contacts={p.contacts}
            onRemove={p.onRemoveGroupMember}
            onSetRole={p.onSetGroupRole}
            onAddMembers={p.onAddGroupMembers}
            onRename={p.onRenameGroup}
            chatControls={
              p.activeConv
                ? {
                    conversation: p.activeConv,
                    disappearTimer: p.disappearTimer,
                    onTogglePin: p.onTogglePin,
                    onToggleMute: p.onToggleMute,
                    onArchive: p.onArchive,
                    onDisappearChange: p.onDisappearChange,
                    onToggleChatLock: p.onToggleChatLock,
                    onClearMessages: p.onClearActiveMessages,
                    onDeleteChat: p.onDeleteActiveChat,
                    onShowStarred: p.loadStarred
                      ? () => setStarredFor(p.activeConv?.id ?? null)
                      : undefined,
                  }
                : undefined
            }
          />
        </ModalShell>
      )}

      {p.showAdd && (
        <AddContactModal
          myInviteLink={myInviteLink}
          myDisplayName={p.account?.displayName ?? "Veto user"}
          contacts={p.contacts}
          onClose={() => p.setShowAdd(false)}
          onAdd={p.onAddContact}
          onOpenContact={p.onOpenContact}
        />
      )}
      {p.showCreateGroup && (
        <CreateGroupModal
          contacts={p.contacts}
          onClose={() => p.setShowCreateGroup(false)}
          onCreate={p.onCreateGroup}
        />
      )}
      {p.showSettings && p.account && (
        <SettingsPanel
          account={p.account}
          tab={p.settingsTab}
          onTab={p.setSettingsTab}
          onClose={() => p.setShowSettings(false)}
          contacts={p.contacts}
          conversations={p.conversations}
          openSecurityCount={p.openSecurityCount}
          securityEvents={p.securityEvents}
          relayStatus={p.relayStatus}
          relayClient={p.relayClient}
          vaultCaps={p.vaultCaps}
          notificationPrefs={p.notificationPrefs}
          notificationPermission={p.notificationPermission}
          onNotificationPrefsChange={setNotifyPrefs}
          onRequestNotificationPermission={requestNotifications}
          onLockNow={p.onLockNow}
          onToast={p.setToast}
          sessionIdentity={p.sessionIdentity}
          onOpenProfile={() => setShowProfile(true)}
          onInvite={() => {
            p.setShowSettings(false);
            p.setShowAdd(true);
          }}
          onRefresh={p.refresh}
          onVerifyContact={async (contact) => {
            await saveContact({ ...contact, verificationState: "verified" });
            // Same recovery as the details sheet: drop a stale key-change preview (FR-CON-003).
            const conv = p.conversations.find(
              (c) => c.type === "direct" && c.contactId === contact.id,
            );
            if (conv?.lastMessagePreview.startsWith("⚠")) {
              await upsertConversation({
                ...conv,
                lastMessagePreview: "Chat ready — safety improved",
              });
            }
            await p.refresh?.();
            p.setToast(`${contact.displayName} verified — safety improved`);
          }}
          mediaCacheCount={p.mediaCacheCount}
          onClearMediaCache={p.onClearMediaCache}
          onShowStarred={
            p.loadStarred
              ? () => {
                  p.setShowSettings(false);
                  setStarredFor(null);
                }
              : undefined
          }
        />
      )}
      {showProfile && p.account && (
        <ProfileModal
          account={p.account}
          onClose={() => setShowProfile(false)}
          onSaveProfile={p.onSaveProfile}
          onToast={p.setToast}
        />
      )}
      {askNotify && (
        <NotificationPrompt
          onTurnOn={async () => {
            markNotificationPromptShown();
            const perm = await requestNotifications();
            setNotifyPrefs({ ...p.notificationPrefs, enabled: perm === "granted" });
            setAskNotify(false);
          }}
          onNotNow={() => {
            markNotificationPromptShown();
            setNotifyPrefs({ ...p.notificationPrefs, enabled: false });
            setAskNotify(false);
          }}
        />
      )}
      <ConfirmHost />
      {reporting && p.activeContact && p.onReportBlock && (
        <ReportModal
          contact={p.activeContact}
          messages={p.messages}
          reported={reporting.reported}
          onClose={() => setReporting(null)}
          onConfirm={() => p.onReportBlock!(p.activeContact!, !!reporting.fromRequest)}
        />
      )}
      {starredFor !== undefined && p.loadStarred && (
        <StarredModal
          conversationId={starredFor}
          load={p.loadStarred}
          onClose={() => setStarredFor(undefined)}
          onOpen={(hit) => {
            setStarredFor(undefined);
            p.setShowDetails(() => false);
            void Promise.resolve(p.onOpenConversation(hit.conversationId)).then(() =>
              setFocusMessageId(hit.eventId),
            );
          }}
        />
      )}
      {forwarding && p.onForwardTo && (
        <ForwardModal
          message={forwarding}
          conversations={p.conversations}
          contacts={p.contacts}
          onClose={() => setForwarding(null)}
          onForward={(targets) => {
            p.onForwardTo?.(forwarding, targets);
            setForwarding(null);
          }}
        />
      )}
      {p.banner && (
        <InAppBanner
          title={p.banner.title}
          body={p.banner.body}
          onOpen={() => {
            p.onOpenConversation(p.banner!.conversationId);
            p.setBanner(null);
          }}
          onDismiss={() => p.setBanner(null)}
        />
      )}
      {p.toast && (
        <div className="toast" role="status">
          {p.toast}
        </div>
      )}
    </div>
  );
}
